import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Clock, Tag, ChevronRight } from "lucide-react";
import { api } from "../../lib/api";
import { cacheGet, cacheSet } from "../../lib/cache";
import BackButton from "../../components/BackButton";
import { Spinner, formatRupiah } from "../../components/ui";
import { WorkerCard } from "../../components/marketplace";
import { useAuth } from "../../context/AuthContext";

export default function CustomerServiceDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const city = user?.profile?.city || "";
  const cacheKey = `svc:${slug}:${city}`;
  const [service, setService] = useState(() => cacheGet(cacheKey).data);
  const [loading, setLoading] = useState(() => service === null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const key = `svc:${slug}:${city}`;
    const fetchService = async () => {
      try {
        const q = city ? `?city=${encodeURIComponent(city)}` : "";
        const { data } = await api.get(`/api/services/${slug}${q}`);
        setService(data);
        cacheSet(key, data, 300000);
        setError(null);
      } catch (err) {
        if (!cacheGet(key).data) setError("Layanan tidak ditemukan.");
      } finally {
        setLoading(false);
      }
    };
    fetchService();
  }, [slug, city]);

  if (loading) return <Spinner />;
  if (error || !service) {
    return (
      <div className="animate-fade-up grid place-items-center py-8">
        <div className="container space-y-6">
          <BackButton to="/customer/services" />
          <div className="card p-10 text-center">
            <p className="font-bold text-brand-800">{error || "Layanan tidak ditemukan"}</p>
            <Link to="/customer/services" className="btn-primary mt-4 inline-flex">
              Kembali ke layanan
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const pricingLabel = {
    FIXED: "Harga Tetap",
    STARTING_FROM: "Mulai dari",
    QUOTATION: "Negosiasi dengan pekerja",
  };

  return (
    <div className="animate-fade-up grid place-items-center py-8">
      <div className="container space-y-6">
        <BackButton to="/customer/services" />

        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-brand-400">
          <Link to="/customer/services" className="hover:text-brand-600 transition">
            Jasa
          </Link>
          <ChevronRight className="h-3.5 w-3.5" />
          {service.category && (
            <>
              <Link
                to={`/customer/services?category=${service.category.slug}`}
                className="hover:text-brand-600 transition"
              >
                {service.category.name}
              </Link>
              <ChevronRight className="h-3.5 w-3.5" />
            </>
          )}
          <span className="text-brand-700 font-medium">{service.name}</span>
        </div>

        {/* Service Info */}
        <div className="card p-6">
          <h1 className="text-brand-800 text-2xl">{service.name}</h1>
          {service.category && (
            <span className="chip bg-brand-50 text-brand-600 text-sm mt-2 inline-flex">
              {service.category.name}
            </span>
          )}
          <p className="text-brand-500 mt-4 leading-relaxed">
            {service.description || "Tidak ada deskripsi untuk layanan ini."}
          </p>
          <div className="flex items-center gap-6 mt-6 pt-4 border-t border-brand-50">
            <div>
              <p className="text-sm text-brand-400">Harga</p>
              <p className="text-2xl font-extrabold text-brand-800">
                {formatRupiah(service.base_price)}
              </p>
              <p className="text-xs text-brand-400">
                {pricingLabel[service.pricing_type] || ""}
              </p>
            </div>
            {service.duration_estimate && (
              <div className="flex items-center gap-2 text-brand-500">
                <Clock className="h-5 w-5" />
                <span className="font-medium">{service.duration_estimate}</span>
              </div>
            )}
          </div>
        </div>

        {/* Workers */}
        <div>
          <h2 className="text-brand-800">
            Pekerja Tersedia ({service.workers?.length || 0})
          </h2>
          <p className="text-brand-400 mt-1">
            {service.ranking === "tier_rating_city"
              ? `Diurutkan: tier tertinggi, rating terbaik${service.customer_city ? `, lalu terdekat di ${service.customer_city}` : " (atur kota profil untuk prioritas terdekat)"}`
              : "Pilih pekerja untuk layanan ini"}
          </p>

          {service.workers && service.workers.length > 0 ? (
            <div className="space-y-3 mt-4">
              {service.workers.map((w) => (
                <WorkerCard key={w.worker_service_id} worker={w} serviceSlug={service.slug} />
              ))}
            </div>
          ) : (
            <div className="card p-8 text-center mt-4">
              <Tag className="h-10 w-10 text-brand-200 mx-auto" />
              <p className="font-bold text-brand-800 mt-3">Belum ada pekerja</p>
              <p className="text-brand-400 mt-1">
                Pekerja belum tersedia untuk layanan ini. Coba lagi nanti.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
